import axios, { AxiosResponse } from "axios";
import { inject, injectable } from "inversify";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { IUserRepository } from "@domain/repositories";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { ChangeMyPassword } from "@domain/schemas/BI";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import type { PagedUserSummaryList, UserSummary } from "@domain/schemas/workspace";

@injectable()
export class UserRepository implements IUserRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async changeMyPassword(changeMyPasswordRequest: ChangeMyPassword): Promise<AxiosResponse> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.changeCurrentUserPassword}`;
    return this._requestHandlerService.handleRequestAsync(() =>
      axios.put(url, changeMyPasswordRequest, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }),
    );
  }

  public async searchUsersAsync(searchTerm: string, page?: number, pageSize?: number): Promise<PagedUserSummaryList> {
    const params = new URLSearchParams({ search: searchTerm });
    if (page !== undefined) params.set("page", String(page));
    if (pageSize !== undefined) params.set("pageSize", String(pageSize));
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.users}?${params.toString()}`;
    return this._requestHandlerService.handleRequestAsync<PagedUserSummaryList>(() => axios.get<PagedUserSummaryList>(url));
  }

  public async getUsersByIdsAsync(userIds: string[]): Promise<UserSummary[]> {
    if (userIds.length === 0) return [];
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.users}?ids=${encodeURIComponent(userIds.join(","))}`;
    return this._requestHandlerService.handleRequestAsync<UserSummary[]>(() => axios.get<UserSummary[]>(url));
  }
}
